/**
 * Country reference data (Sprint countries.ts).
 *
 * Single source of truth for the full ISO 3166-1 country list +
 * E.164 calling codes. Replaces hardcoded per-form country arrays
 * (RegisterPage had 11). The platform is worldwide.
 *
 * Data is derived, never hardcoded:
 *  - codes + calling codes come from libphonenumber-js (getCountries,
 *    getCountryCallingCode) -- always in sync with the library, no
 *    risk of a stale or wrong prefix;
 *  - display names come from the runtime Intl.DisplayNames API
 *    (built into CF Workers and modern browsers, zero dependency),
 *    so names are localized for free.
 */

import { getCountries, getCountryCallingCode } from "libphonenumber-js/min";
import type { CountryCode } from "libphonenumber-js/min";

export type Country = {
  /** ISO 3166-1 alpha-2, uppercase (e.g. "RO"). */
  code: CountryCode;
  /** Localized display name (e.g. "Romania" / "România"). */
  name: string;
  /** E.164 calling code, digits only, no leading "+" (e.g. "40"). */
  dialPrefix: string;
};

/**
 * Build the full country list, localized + sorted by display name.
 *
 * @param locale - BCP-47 locale for names ("en", "ro", ...).
 *                 Defaults to "en". Unknown locales fall back to
 *                 the runtime default.
 *
 * The result is computed on each call; callers that render often
 * (e.g. a dropdown) should memoize. ~250 entries, cheap to build.
 */
export function getCountryList(locale = "en"): Country[] {
  let displayNames: Intl.DisplayNames | null = null;
  try {
    displayNames = new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    // Invalid locale -> names fall back to the raw code below.
    displayNames = null;
  }

  const list: Country[] = [];
  for (const code of getCountries()) {
    let name = code as string;
    if (displayNames) {
      try {
        name = displayNames.of(code) ?? code;
      } catch {
        name = code;
      }
    }
    let dialPrefix = "";
    try {
      dialPrefix = getCountryCallingCode(code);
    } catch {
      // A country with no calling code in the library -- skip it,
      // it cannot be used for phone entry anyway.
      continue;
    }
    list.push({ code, name, dialPrefix });
  }

  // Sort by localized name, locale-aware.
  list.sort((a, b) => a.name.localeCompare(b.name, locale));
  return list;
}

/**
 * E.164 calling code for one country code, or null if unknown.
 * Use for phone normalization (national -> international).
 */
export function getDialPrefix(code: string): string | null {
  try {
    return getCountryCallingCode(code.toUpperCase() as CountryCode);
  } catch {
    return null;
  }
}
